import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box} from "@mui/system";
import {
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import _ from "lodash";
import {organizationSettingsService} from "_services";
import {alertMessages} from "_constants";
import {Auth} from "components/Authorization";
import {SnackBar} from "components/common";
import BasicModal from "../../../common/BasicModal";
import BasicTable from "../../../common/BasicTable";
import styles from "../organizationMainStyles";
import {fragmentId} from "../Utilities/organizationSettingsConstants";
import {getLabel} from "../Utilities/organizationSettingsUtils";

const PAGE_SIZE = 10;

const blacklistTabs = [
    {value: "facebook", label: "Facebook", valueKey: "domain", modalLabel: "URL", appliesGlobally: true},
    {value: "instagram", label: "Instagram", valueKey: "domain", modalLabel: "URL", appliesGlobally: true},
    {value: "domain", label: "Domain", valueKey: "domain", modalLabel: "Domain", appliesGlobally: true},
    {value: "recipient", label: "Recipient", valueKey: "recipient", modalLabel: "Recipient email", appliesGlobally: false},
    {value: "email", label: "Email", valueKey: "email", modalLabel: "Email address", appliesGlobally: true},
];

const tableStyles = {
    tableCell: {
        fontFamily: "LarsseitRegular",
        fontSize: "14px",
        fontWeight: 500,
        color: "#0C2728",
        padding: "8px 5px",
        "&:first-of-type": {
            paddingLeft: "18px",
        },
        "&:last-of-type": {
            paddingRight: "18px",
        },
    },
    tableHead: {
        fontFamily: "LarsseitMedium",
    },
};

const tabStyles = {
    minHeight: 42,
    "& .MuiTab-root": {
        fontFamily: "LarsseitMedium",
        fontSize: "14px",
        textTransform: "none",
        color: "#495D5E",
    },
    "& .Mui-selected": {
        color: "#3081ED",
    },
    "& .MuiTabs-indicator": {
        backgroundColor: "#3081ED",
    },
};

const paginationStyles = {
    bar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        pt: 1,
    },
    label: {
        fontFamily: "LarsseitMedium",
        fontSize: "14px",
        color: "#495D5E",
    },
};

const primaryButtonSx = {
    textTransform: "none",
    fontFamily: "LarsseitMedium",
    bgcolor: "#003300",
    "&:hover": {
        bgcolor: "#004d00",
    },
    "&:disabled": {
        bgcolor: "rgba(0, 51, 0, 0.35)",
        color: "#FFFFFF",
    },
};

const secondaryButtonSx = {
    textTransform: "none",
    fontFamily: "LarsseitMedium",
    color: "#0C2728",
    borderColor: "#CED4D4",
};

function formatDate(value) {
    if (!value) {
        return "—";
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

async function fetchBlacklistPage(resource, page) {
    const res = await organizationSettingsService.listBlacklist(resource, {
        max: PAGE_SIZE + 1,
        offset: page * PAGE_SIZE,
    });
    const data = Array.isArray(res.data) ? res.data : [];
    return {
        rows: data.slice(0, PAGE_SIZE),
        hasNextPage: data.length > PAGE_SIZE,
    };
}

function handleRequestError(error) {
    if (_.get(error, "response.status") === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, "response.data.message") || alertMessages.GENERIC_ERROR;
    SnackBar.showMessage(message);
}

export default function Blacklists({organizationId, organizationName, organizationTrendsId}) {
    const label = getLabel(fragmentId.BLACKLISTS);
    const sectionTitle = _.get(label, "BLACKLISTS", "Blacklists");

    const [selectedTab, setSelectedTab] = useState(blacklistTabs[0].value);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [primaryValue, setPrimaryValue] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);

    const tabMeta = useMemo(
        () => blacklistTabs.find((t) => t.value === selectedTab) || blacklistTabs[0],
        [selectedTab]
    );

    const loadRows = useCallback(async () => {
        setLoading(true);
        let result;
        try {
            result = await fetchBlacklistPage(selectedTab, page);
        } catch (e) {
            setRows([]);
            setHasNextPage(false);
            handleRequestError(e);
            setLoading(false);
            return;
        }
        if (result.rows.length === 0 && page > 0) {
            setPage(page - 1);
            return;
        }
        setRows(result.rows);
        setHasNextPage(result.hasNextPage);
        setLoading(false);
    }, [selectedTab, page]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const handleTabChange = (_, newValue) => {
        setSelectedTab(newValue);
        setPrimaryValue("");
        setPage(0);
        setHasNextPage(false);
        setDeleteConfirmTarget(null);
    };

    const handlePrevPage = () => {
        if (page > 0 && !loading) {
            setPage(page - 1);
        }
    };

    const handleNextPage = () => {
        if (hasNextPage && !loading) {
            setPage(page + 1);
        }
    };

    const organizationSelected = !_.isEmpty(organizationId);
    const recipientAddBlocked = selectedTab === "recipient" && !organizationSelected;

    const openAddModal = () => {
        if (selectedTab === "recipient" && !organizationSelected) {
            SnackBar.showMessage("Select an organization first.");
            return;
        }
        setPrimaryValue("");
        setAddOpen(true);
    };

    const closeAddModal = () => {
        if (!submitting) {
            setAddOpen(false);
        }
    };

    const buildCreatePayload = () => {
        const key = tabMeta.valueKey;
        const payload = {
            [key]: primaryValue.trim(),
        };
        if (selectedTab === "recipient" && organizationSelected) {
            const trendsId = Number(organizationTrendsId);
            payload.organizationId = Number.isFinite(trendsId) ? trendsId : organizationTrendsId;
        }
        return payload;
    };

    const handleCreate = async () => {
        const trimmed = primaryValue.trim();
        if (!trimmed) {
            SnackBar.showMessage("Please enter a value.");
            return;
        }
        if (selectedTab === "recipient" && !organizationSelected) {
            SnackBar.showMessage("Select an organization first.");
            return;
        }
        setSubmitting(true);
        try {
            await organizationSettingsService.createBlacklistEntry(selectedTab, buildCreatePayload());
            SnackBar.showMessage("Blacklist entry added.");
            setAddOpen(false);
            setPrimaryValue("");
            await loadRows();
        } catch (e) {
            handleRequestError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const executeBlacklistDelete = useCallback(
        async (id) => {
            if (!id && id !== 0) {
                return false;
            }
            setDeletingId(id);
            try {
                await organizationSettingsService.deleteBlacklistEntry(selectedTab, id);
                SnackBar.showMessage("Blacklist entry removed.");
                await loadRows();
                return true;
            } catch (e) {
                handleRequestError(e);
                return false;
            } finally {
                setDeletingId(null);
            }
        },
        [selectedTab, loadRows]
    );

    const openDeleteBlacklistConfirm = useCallback(
        (row) => {
            if (row == null || row.id == null) {
                return;
            }
            const rawValue = row[tabMeta.valueKey] ?? row.recipient ?? "—";
            setDeleteConfirmTarget({
                id: row.id,
                value: rawValue != null ? String(rawValue) : "—",
            });
        },
        [tabMeta.valueKey]
    );

    const closeDeleteBlacklistConfirm = useCallback(() => {
        if (deletingId == null) {
            setDeleteConfirmTarget(null);
        }
    }, [deletingId]);

    const confirmBlacklistDelete = useCallback(async () => {
        if (deleteConfirmTarget == null) {
            return;
        }
        const id = deleteConfirmTarget.id;
        const ok = await executeBlacklistDelete(id);
        if (ok) {
            setDeleteConfirmTarget(null);
        }
    }, [deleteConfirmTarget, executeBlacklistDelete]);

    const valueColumnLabel =
        selectedTab === "facebook" || selectedTab === "instagram"
            ? "URL"
            : tabMeta.modalLabel;

    const showGlobalScopeNotice = tabMeta.appliesGlobally === true;
    const showRecipientOrgHint = selectedTab === "recipient" && !organizationSelected;
    const showRecipientOrgScopeNotice = selectedTab === "recipient" && organizationSelected;

    const recipientOrganizationLabel = useMemo(() => {
        if (organizationName && String(organizationName).trim()) {
            return String(organizationName).trim();
        }
        if (organizationTrendsId != null && organizationTrendsId !== "") {
            return `Trends organization ${organizationTrendsId}`;
        }
        if (!_.isEmpty(organizationId)) {
            return `Organization ${organizationId}`;
        }
        return "the selected organization";
    }, [organizationName, organizationTrendsId, organizationId]);

    const tableColumns = useMemo(
        () => [
            {id: "entryId", label: "ID"},
            {id: "dateCreated", label: "Date created"},
            {id: "value", label: valueColumnLabel},
            {id: "actions", label: "Actions", align: "right"},
        ],
        [valueColumnLabel]
    );

    const tableRows = useMemo(
        () =>
            rows.map((row) => ({
                entryId: row.id,
                dateCreated: formatDate(row.dateCreated),
                value: row[tabMeta.valueKey] ?? row.recipient ?? "—",
                actions: (
                    <IconButton
                        aria-label="Remove blacklist entry"
                        size="small"
                        disabled={deletingId === row.id}
                        onClick={(e) => {
                            e.stopPropagation();
                            openDeleteBlacklistConfirm(row);
                        }}
                        sx={{color: "#495D5E"}}
                    >
                        <DeleteOutlineIcon fontSize="small"/>
                    </IconButton>
                ),
            })),
        [rows, tabMeta.valueKey, deletingId, openDeleteBlacklistConfirm]
    );

    const loadingBody = (
        <Box sx={styles.bodyLoading}>
            <CircularProgress sx={styles.bodyLoadingSpinner}/>
        </Box>
    );

    return (
        <Box sx={styles.sectionWrapper}>
            <Box component="h2" sx={{...styles.heading, mb: 0}}>
                {sectionTitle}
            </Box>
            <Divider orientation="horizontal" flexItem sx={styles.sectionDivider}/>
            <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                aria-label="Blacklist tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{...tabStyles, mt: 0}}
            >
                {blacklistTabs.map((tab) => (
                    <Tab key={tab.value} value={tab.value} label={tab.label}/>
                ))}
            </Tabs>
            <Box sx={{pt: 2}}>
                {showGlobalScopeNotice && (
                    <Box component="p" sx={{...styles.infoItem, m: 0, mb: 2}}>
                        These blacklist entries are global: they apply across all organizations.
                    </Box>
                )}
                {showRecipientOrgHint && (
                    <Box component="p" sx={{...styles.infoItem, m: 0, mb: 2}}>
                        Select an organization above before adding a recipient blacklist entry.
                    </Box>
                )}
                {showRecipientOrgScopeNotice && (
                    <Box
                        component="p"
                        sx={{
                            m: 0,
                            mb: 2,
                            py: 1.5,
                            px: 2,
                            borderRadius: "5px",
                            border: "1px solid #CED4D4",
                            bgcolor: "#F8F9F9",
                            fontFamily: "larsseitRegular",
                            fontSize: {xs: "15px", md: "16px"},
                            color: "#495D5E",
                        }}
                    >
                        Phrases of blacklisted recipient's messages will be hidden from dashboard of the selected organization:{" "}
                        <Box component="span" sx={{fontFamily: "LarsseitMedium", color: "#0C2728"}}>
                            {recipientOrganizationLabel}
                        </Box>
                        {organizationTrendsId != null &&
                            organizationTrendsId !== "" &&
                            organizationName &&
                            String(organizationName).trim() && (
                            <>
                                {" "}
                                (Trends organization ID:{" "}
                                <Box component="span" sx={{fontFamily: "LarsseitMedium", color: "#0C2728"}}>
                                    {organizationTrendsId}
                                </Box>
                                )
                            </>
                        )}
                        . Blacklist entries themselves are global: they apply across all organizations.
                    </Box>
                )}
                <Box sx={{mb: 2}}>
                    <Button variant="contained" onClick={openAddModal} disabled={recipientAddBlocked} sx={primaryButtonSx}>
                        Add blacklist entry
                    </Button>
                </Box>
                {loading ? (
                    loadingBody
                ) : rows.length === 0 ? (
                    <Typography sx={styles.infoItem}>No entries.</Typography>
                ) : (
                    <>
                        <BasicTable
                            rows={tableRows}
                            columns={tableColumns}
                            withPagination={false}
                            paperStyle={{borderRadius: "5px", boxShadow: "none", border: "1px solid #EEEFEF"}}
                            tableCellStyle={tableStyles.tableCell}
                            tableHeadStyle={tableStyles.tableHead}
                        />
                        <Box sx={paginationStyles.bar}>
                            <IconButton
                                aria-label="Previous page"
                                size="small"
                                onClick={handlePrevPage}
                                disabled={page === 0 || loading}
                            >
                                <KeyboardArrowLeftIcon fontSize="small"/>
                            </IconButton>
                            <Typography sx={paginationStyles.label}>Page {page + 1}</Typography>
                            <IconButton
                                aria-label="Next page"
                                size="small"
                                onClick={handleNextPage}
                                disabled={!hasNextPage || loading}
                            >
                                <KeyboardArrowRightIcon fontSize="small"/>
                            </IconButton>
                        </Box>
                    </>
                )}
            </Box>
            <BasicModal
                open={addOpen}
                handleClose={closeAddModal}
                title={`Add ${tabMeta.label} blacklist entry`}
                style={{width: "480px"}}
            >
                <Box sx={{display: "flex", flexDirection: "column", gap: 2, pt: 1}}>
                    {selectedTab === "recipient" && organizationSelected && (
                        <Typography sx={{...styles.infoItem, m: 0}}>
                            Phrases of blacklisted recipient's messages will be hidden from dashboard of{" "}
                            <Box component="span" sx={{fontFamily: "LarsseitMedium", color: "#0C2728"}}>
                                {recipientOrganizationLabel}
                            </Box>
                            . Blacklist entries themselves are global: they apply across all organizations.
                        </Typography>
                    )}
                    <TextField
                        label={tabMeta.modalLabel}
                        value={primaryValue}
                        onChange={(e) => setPrimaryValue(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={submitting}
                        InputLabelProps={{sx: {fontFamily: "LarsseitMedium", fontSize: "14px"}}}
                        InputProps={{sx: {"& input": {fontFamily: "larsseitRegular", fontSize: "15px"}}}}
                    />
                    <Box sx={{display: "flex", justifyContent: "flex-end", gap: 1, mt: 1}}>
                        <Button onClick={closeAddModal} disabled={submitting} sx={{textTransform: "none", fontFamily: "LarsseitMedium"}}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={submitting}
                            sx={primaryButtonSx}
                        >
                            {submitting ? "Saving…" : "Save"}
                        </Button>
                    </Box>
                </Box>
            </BasicModal>
            <BasicModal
                open={deleteConfirmTarget != null}
                handleClose={closeDeleteBlacklistConfirm}
                title={`Remove ${tabMeta.label} blacklist entry?`}
                style={{width: "440px", maxWidth: "calc(100vw - 32px)"}}
            >
                <Box sx={{display: "flex", flexDirection: "column", pt: 1}}>
                    <Typography sx={{fontFamily: "LarsseitRegular", fontSize: "0.875rem", color: "#0C2728"}}>
                        You are about to permanently remove a blacklist entry.
                    </Typography>
                    <Box
                        sx={{
                            my: 2,
                            py: 1.25,
                            px: 1.5,
                            borderRadius: "5px",
                            border: "1px solid #EEEFEF",
                            bgcolor: "#F8F9F9",
                        }}
                    >
                        <Typography
                            component="div"
                            sx={{fontFamily: "LarsseitMedium", fontSize: "0.875rem", color: "#0C2728", lineHeight: 1.5}}
                        >
                            ID {deleteConfirmTarget?.id}
                        </Typography>
                        {deleteConfirmTarget?.value ? (
                            <Typography
                                component="div"
                                sx={{
                                    fontFamily: "LarsseitRegular",
                                    fontSize: "0.875rem",
                                    color: "#0C2728",
                                    lineHeight: 1.5,
                                    mt: 0.75,
                                    wordBreak: "break-word",
                                }}
                            >
                                {valueColumnLabel}: {deleteConfirmTarget.value}
                            </Typography>
                        ) : null}
                    </Box>
                    <Typography sx={{fontFamily: "LarsseitRegular", fontSize: "0.875rem", color: "#0C2728"}}>
                        This cannot be undone.
                    </Typography>
                    <Box sx={{display: "flex", justifyContent: "flex-end", gap: 1, mt: 2}}>
                        <Button
                            variant="outlined"
                            onClick={closeDeleteBlacklistConfirm}
                            disabled={deletingId != null}
                            sx={secondaryButtonSx}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={confirmBlacklistDelete}
                            disabled={deletingId != null}
                            sx={{textTransform: "none", fontFamily: "LarsseitMedium"}}
                        >
                            {deletingId != null ? "Removing…" : "Remove"}
                        </Button>
                    </Box>
                </Box>
            </BasicModal>
        </Box>
    );
}
